import { LocaleEnum } from "lopermut"
import { z } from "zod"
import { OrganizationTypeEnum } from "../../companies/dtos"
import { createFilterBySchema } from "../../shared/filter_dtos"
import { PaginationSchema } from "../../shared/pagination_dtos"
import { QuerySchema } from "../../shared/query_dtos"
import { createSortSchema } from "../../shared/sort_dtos"

export const BackofficeCompaniesSortFields = ["id", "name", "slug", "createdAt"] as const
export type BackofficeCompaniesSortFieldsEnum = (typeof BackofficeCompaniesSortFields)[number]
export const BackofficeCompaniesSortSchema = createSortSchema(
  BackofficeCompaniesSortFields,
  "id",
  "asc",
)

export const BackofficeCompaniesFilterBySchema = createFilterBySchema([
  {
    key: "id",
    value: z.string().optional(),
  },
  {
    key: "name",
    value: z.string().optional(),
  },
  {
    key: "slug",
    value: z.string().optional(),
  },
  {
    key: "type",
    value: z.array(z.enum(OrganizationTypeEnum)).optional(),
  },
  {
    key: "hasAmphoraIntegration",
    value: z
      .union([z.boolean(), z.string()])
      .transform((value) => value === true || value === "true")
      .optional(),
  },
  {
    key: "hasShopifyIntegration",
    value: z
      .union([z.boolean(), z.string()])
      .transform((value) => value === true || value === "true")
      .optional(),
  },
])

export const GetBackofficeCompaniesSchema = z.object({
  ...PaginationSchema.extend(QuerySchema.shape)
    .extend(BackofficeCompaniesSortSchema.shape)
    .extend(BackofficeCompaniesFilterBySchema.shape).shape,
})

export const GetBackofficeCompanyParamsSchema = z.object({
  id: z.string().transform(BigInt),
})

export const AddCreditsPackageBodySchema = z.object({
  credits: z.number(),
})

export const CreateBackofficeCompanyFromWebsiteBodySchema = z.object({
  company: z.object({
    name: z.string(),
    locale: LocaleEnum,
    website: z.string().url(),
  }),
})

export const CreateBackofficeCompanyBodySchema = z
  .object({
    organizationId: z.string().optional(),
    company: z.object({
      name: z.string(),
      logo: z.string().optional(),
      locale: LocaleEnum,
      slug: z.string(),
    }),
    employee: z
      .object({
        name: z.string(),
        email: z.string(),
        password: z.string(),
      })
      .optional(),
    customer: z
      .object({
        name: z.string().optional(),
        phone: z.string().optional(),
        locale: LocaleEnum.optional(),
      })
      .optional()
      .refine(
        (data) => {
          if (!data) return true
          const hasAny = data.name || data.phone || data.locale
          const hasAll = data.name && data.phone && data.locale
          return !hasAny || hasAll
        },
        {
          error: "All customer fields are required if providing customer information",
        },
      ),
  })
  .refine((data) => data.organizationId || data.employee, {
    path: ["employee"],
    error: "Employee information is required when creating a new organization",
  })
